import { Add, Close, DeleteOutline } from '@mui/icons-material';
import { Box, IconButton, Stack } from '@mui/material';
import { useCallback, useEffect } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { validDepartmentNamePattern } from '../../../constants/agentConstants';
import { AnalyticsEvents } from '../../../enums/AnalyticsEvents';
import { useAnalytics } from '../../../hooks/useAnalytics';
import { useTranslation } from '../../../hooks/useTranslation';
import { AgentCapability } from '../../../interfaces/Agent';
import StyledFormItem from '../../../styles/StyledFormItem';
import Button from '../../shared/Button/Button';
import Input from '../../shared/Input/Input';
import TelephoneInput from '../../shared/TelephoneInput/TelephoneInput';
import Text from '../../shared/Text/Text';
import EmployeeDirectoryToggle from './EmployeeDirectoryToggle';

interface TransferCallsError {
  department_name?: {
    message?: string;
    type?: string;
  };
  email?: {
    message?: string;
    type?: string;
  };
}

interface FormErrors {
  transfer_calls?: TransferCallsError[];
}

interface TransferCallsCapabilityProps {
  maxTransferCalls: number;
  usedAgentPhoneNumbers?: (string | undefined)[];
  onToggleEmployeeDirectory?: () => void;
  disableEmployeeDirectoryToggle?: boolean;
}

const TransferCallsCapabilityV1: React.FC<TransferCallsCapabilityProps> = ({
  maxTransferCalls,
  usedAgentPhoneNumbers = [],
  onToggleEmployeeDirectory,
  disableEmployeeDirectoryToggle = false,
}) => {
  const {
    setValue,
    control,
    formState: { errors },
    watch,
    clearErrors,
  } = useFormContext();

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'transfer_calls',
  });

  const { track } = useAnalytics();
  const { t } = useTranslation();

  const formErrors = errors as unknown as FormErrors;

  const selectedCapability = watch('external_capabilities', '');

  // Check if we should display transfer calls based on capability selection or existing data
  const transferCalls = watch('transfer_calls');
  const shouldDisplayTransferCalls =
    selectedCapability === 'transfer_calls' ||
    (Array.isArray(selectedCapability) &&
      selectedCapability.includes('transfer_calls')) ||
    (Array.isArray(transferCalls) && transferCalls.length > 0);

  const handleAddTransferCall = useCallback(() => {
    if (fields.length < maxTransferCalls) {
      append({ department_name: '', phone_number: '+352', email: '' });
      track(AnalyticsEvents.AGENT_TRANSFER_CALLS_CONFIGURED, {
        action: 'add',
        count: fields.length + 1,
      });
    }
  }, [append, fields.length, maxTransferCalls, track]);

  const handleRemoveTransferCall = useCallback(
    (index: number) => {
      remove(index);
      track(AnalyticsEvents.AGENT_TRANSFER_CALLS_CONFIGURED, {
        action: 'remove',
        count: fields.length - 1,
      });

      // Check if this will be the last item being removed
      if (fields.length === 1) {
        setValue('external_capabilities', '');
        setValue('transfer_calls', []);
      }
    },
    [remove, fields.length, setValue, track]
  );

  const handleRemoveCapability = () => {
    // Clear the entire transfer calls capability
    setValue('transfer_calls', []);
    setValue('external_capabilities', '');

    clearErrors('transfer_calls');

    // Clear sessionStorage data for this capability
    try {
      const currentState = JSON.parse(
        sessionStorage.getItem('elora_agent_form_state') || '{}'
      );
      const updatedState = {
        ...currentState,
        transfer_calls: [],
        external_capabilities: '',
      };
      sessionStorage.setItem(
        'elora_agent_form_state',
        JSON.stringify(updatedState)
      );
    } catch (error) {
      console.warn('Failed to update sessionStorage:', error);
    }

    // Track the removal
    track(AnalyticsEvents.AGENT_CAPABILITY_MODIFIED, {
      action: 'removed',
      capability: AgentCapability.TRANSFER_CALLS,
    });
  };

  const handleDepartmentChange = useCallback(
    (index: number, value: string) => {
      setValue(`transfer_calls.${index}.department_name`, value);
      track(AnalyticsEvents.AGENT_DEPARTMENT_SELECTED, {
        department: value,
        index,
      });
    },
    [setValue, track]
  );

  useEffect(() => {
    if (!shouldDisplayTransferCalls) {
      setValue('external_capabilities', '');
      setValue('transfer_calls', []);
    }
  }, [shouldDisplayTransferCalls, setValue]);

  // Populate fields when editing an existing agent with transfer calls
  // This only happens when the component is rendered due to existing data
  useEffect(() => {
    if (shouldDisplayTransferCalls && fields.length === 0) {
      const formTransferCalls = watch('transfer_calls') || [];
      if (formTransferCalls.length > 0) {
        // The form has data but no fields are displayed, so populate the fields
        // This only happens when editing an existing agent
        formTransferCalls.forEach(
          (transferCall: { department_name: string; phone_number: string }) => {
            append(transferCall);
          }
        );
      }
    }
  }, [shouldDisplayTransferCalls, watch, append, fields.length]);

  useEffect(() => {
    if (fields.length === 0) {
      // Only clear external_capabilities if we're not supposed to display transfer calls
      if (!shouldDisplayTransferCalls) {
        setValue('external_capabilities', '');
      }
    }
  }, [fields, shouldDisplayTransferCalls, setValue]);

  // Don't render anything if the capability is not selected
  if (!shouldDisplayTransferCalls) {
    return null;
  }

  return (
    <>
      <Box
        sx={{
          border: '1px solid lightgrey',
          padding: '1rem',
          borderRadius: '4px',
          backgroundColor: 'white',
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ marginBottom: '1rem' }}
        >
          <Stack direction="row" alignItems="center" gap="1rem">
            <h3 style={{ margin: 0 }}>{t('Transfer Calls')}</h3>
            {onToggleEmployeeDirectory && (
              <EmployeeDirectoryToggle
                checked={false}
                onChange={onToggleEmployeeDirectory}
                disabled={disableEmployeeDirectoryToggle}
              />
            )}
          </Stack>
          {fields.length > 0 && (
            <IconButton
              onClick={handleRemoveCapability}
              sx={{
                height: '32px',
                width: '32px',
                color: 'error.main',
                border: '1px solid',
                borderColor: 'error.main',
                '&:hover': {
                  backgroundColor: 'error.main',
                  color: 'white',
                },
              }}
              title={t('Remove Transfer Calls capability')}
            >
              <Close />
            </IconButton>
          )}
        </Stack>
        {fields.map((field, index: number) => (
          <Box
            key={field.id}
            sx={{
              padding: '12px 16px 4px',
              marginBottom: '1rem',
            }}
          >
            {/* Row 1: Department + Delete button */}
            <Box sx={{ display: 'flex', gap: '1rem', alignItems: 'flex-end' }}>
              <StyledFormItem
                sx={{ flex: 1, marginTop: '8px', marginBottom: '1rem' }}
              >
                <Text type="label">{`Department`}</Text>
                <Input
                  name={`transfer_calls.${index}.department_name`}
                  required
                  maxLength={50}
                  withError={
                    !!formErrors.transfer_calls?.[index]?.department_name
                  }
                  helperText={
                    formErrors.transfer_calls?.[index]?.department_name
                      ?.message ||
                    (!!formErrors.transfer_calls?.[index]?.department_name
                      ? 'Department name is required'
                      : '')
                  }
                  onChange={event =>
                    handleDepartmentChange(index, event.target.value)
                  }
                  pattern={validDepartmentNamePattern}
                />
              </StyledFormItem>
              <IconButton
                color="error"
                sx={{
                  height: '56px',
                  width: '56px',
                  padding: 0,
                  marginBottom: '1rem',
                  borderRadius: '4px',
                  border: '1px solid lightgrey',
                  backgroundColor: 'var(--IconButton-hoverBg)',
                  flexShrink: 0,
                }}
                type="button"
                onClick={() => handleRemoveTransferCall(index)}
              >
                <DeleteOutline fontSize="small" />
              </IconButton>
            </Box>

            {/* Row 2: Phone Number + Email side by side */}
            <Box
              sx={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}
            >
              <Box
                sx={{ flex: 1, '& > div': { marginTop: 0, marginBottom: 0 } }}
              >
                <TelephoneInput
                  name={`transfer_calls.${index}.phone_number`}
                  required
                  usedPhoneNumbers={usedAgentPhoneNumbers}
                  usedPhoneNumbersErrorText="This phone number is currently attached to an agent"
                />
              </Box>
              <StyledFormItem sx={{ flex: 1, marginTop: 0, marginBottom: 0 }}>
                <Text type="label">{`Email`}</Text>
                <Input
                  name={`transfer_calls.${index}.email`}
                  type="email"
                  required
                  withError={!!formErrors.transfer_calls?.[index]?.email}
                  helperText={
                    formErrors.transfer_calls?.[index]?.email?.message ||
                    'Email address is required'
                  }
                />
              </StyledFormItem>
            </Box>
          </Box>
        ))}

        {fields.length > 0 && (
          <Box sx={{ marginTop: '1rem', marginLeft: '1rem' }}>
            <Button
              type="button"
              variant="outlined"
              color="inherit"
              size="small"
              onClick={handleAddTransferCall}
              disabled={fields.length >= maxTransferCalls}
            >
              <Add sx={{ marginRight: '0.5rem' }} />
              {t('Add')}
            </Button>
          </Box>
        )}
      </Box>
    </>
  );
};

export default TransferCallsCapabilityV1;
